// Batch 7-C｜匿名 History Scope（V1）
//
// 同一 browser + 同一 placeId 必須永遠沿用同一個 historyScopeToken（高熵 UUID）。
// 不同 placeId 不得共用同一個 token。
//
// 只保存 { "<placeId>": "<uuid>" }；絕不保存完整 AuditReport、Google Snapshot、
// scoreResult、Peer JSON 或任何報告內容。
//
// localStorage 不可用（privacy mode / storage exception）時退回 in-memory Map，
// 維持「目前 session」的 history scope，健診功能不受影響。
// 此 fallback 不會跨瀏覽器重開保存 → 歷史紀錄可能無法自動找回（V1 已知限制）。
//
// historyScopeToken 是 bearer scope token：不得顯示於 UI、URL、query string、log 或 analytics。

const STORAGE_KEY = 'tdr_gmb_history_scope_v1';

/** localStorage 不可用時的 session 內 fallback（同一 tab session 保持一致）。 */
const memoryFallback = new Map<string, string>();

/** 產生 RFC4122 v4 UUID（crypto.randomUUID 優先，否則手動組裝）。 */
function createUuid(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to manual generation
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}

/** 讀取整個 scope store（只保留 string 值）。失敗一律視為空。 */
function readStore(): Record<string, string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, string> = {};
    Object.keys(parsed).forEach((key) => {
      const value = (parsed as Record<string, unknown>)[key];
      if (typeof value === 'string' && value.length > 0) out[key] = value;
    });
    return out;
  } catch {
    return {};
  }
}

function writeStore(store: Record<string, string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // ignore：memory fallback 仍維持本 session 一致性
  }
}

/**
 * 取得（必要時建立）某 placeId 的 historyScopeToken。
 * 同一 browser + 同一 placeId 永遠回同一個值。placeId 為空時回空字串。
 */
export function getHistoryScopeToken(placeId: string): string {
  const id = (placeId ?? '').trim();
  if (!id) return '';

  const store = readStore();
  if (store[id]) {
    memoryFallback.set(id, store[id]);
    return store[id];
  }

  const mem = memoryFallback.get(id);
  if (mem) return mem;

  const token = createUuid();
  store[id] = token;
  writeStore(store);
  memoryFallback.set(id, token);
  return token;
}